/**
 * O PLANTÃO e as RECUSAS — aba "Conexão e limites" (frentes 8/9 de 11/09).
 *
 * 🔴 O caso que originou os dois: o Matheus escreveu no grupo de teste fora do
 * horário e o bot ficou mudo — e não havia onde configurar o número do
 * plantão, nem onde VER que uma mensagem foi recusada. Aqui ficam os dois:
 * o número (com o aviso do custo enquanto vazio) e as recusas de 48h.
 */
import { useEffect, useState } from 'react';

import { Alert, Button, HelpTip, Input } from '@/components/ui';
import { api } from '@/lib/api';

import { formatarData } from '@/lib/format-date';
interface Recusa {
  gatilho: string;
  motivo: string;
  explicacao: string;
  quandoIso: string;
}

export function PlantaoERecusas({
  telefonePlantao,
  onMudou,
}: {
  telefonePlantao: string | null;
  onMudou: () => void;
}) {
  const [numero, setNumero] = useState(telefonePlantao ?? '');
  const [salvando, setSalvando] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [recusas, setRecusas] = useState<Recusa[] | null>(null);

  useEffect(() => {
    api
      .get<{ recusas: Recusa[] }>('/api/v1/admin/whatsapp/recusas')
      .then((r) => setRecusas(r.recusas))
      .catch(() => setRecusas([]));
  }, []);

  const quando = (iso: string) =>
    formatarData(new Date(/[zZ]|[+-]\d\d:\d\d$/.test(iso) ? iso : `${iso.replace(' ', 'T')}Z`), {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });

  return (
    <div className="space-y-4">
      <div data-testid="wa-plantao">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-ink-1 dark:text-ink-dark-1">
            Telefone do plantão
          </span>
          <HelpTip texto="O número que a resposta automática de fora do horário indica para urgências. Vale para todos os clientes; um contrato pode ter o próprio número, e aí ele vence. Enquanto este campo estiver vazio, a mensagem de plantão não sai — o sistema não manda frase pela metade." />
        </div>
        {!telefonePlantao && (
          <Alert variant="warning" className="mt-2" data-testid="wa-plantao-vazio">
            O aviso de fora do horário <strong>não vai sair</strong> até este número ser
            preenchido — o modelo usa o telefone do plantão, e sem valor a mensagem é recusada.
          </Alert>
        )}
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <Input
            value={numero}
            onChange={(e) => setNumero(e.target.value)}
            placeholder="(19) 99999-0000"
            className="w-56"
            data-testid="wa-plantao-numero"
          />
          <Button
            size="sm"
            loading={salvando}
            data-testid="wa-plantao-salvar"
            onClick={async () => {
              setMsg(null);
              setSalvando(true);
              try {
                await api.put('/api/v1/admin/whatsapp/plantao', { telefone: numero.trim() || null });
                setMsg('Guardado.');
                onMudou();
              } catch {
                setMsg('Não foi possível guardar agora.');
              } finally {
                setSalvando(false);
              }
            }}
          >
            Guardar
          </Button>
          {msg && <span className="text-xs text-ink-3 dark:text-ink-dark-3">{msg}</span>}
        </div>
      </div>

      <div className="border-t border-line pt-4 dark:border-line-dark" data-testid="wa-recusas">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-ink-1 dark:text-ink-dark-1">
            Mensagens recusadas (últimas 48h)
          </span>
          <HelpTip texto="Toda mensagem que o porteiro recusou — com o aviso, o motivo e a hora. Recusa não é defeito: é o sistema se recusando a mandar frase pela metade ou a furar uma regra. Mas recusa invisível parece defeito, e é por isso que ela mora aqui." />
        </div>
        {recusas === null ? (
          <p className="mt-2 text-sm text-ink-3 dark:text-ink-dark-3">Consultando…</p>
        ) : recusas.length === 0 ? (
          <p className="mt-2 text-sm text-ink-3 dark:text-ink-dark-3">
            Nenhuma recusa nas últimas 48 horas.
          </p>
        ) : (
          <ul className="mt-2 space-y-1.5">
            {recusas.map((r, i) => (
              <li key={i} className="text-xs text-ink-2 dark:text-ink-dark-2">
                <span className="font-medium">{quando(r.quandoIso)}</span> ·{' '}
                <span className="font-mono">{r.gatilho}</span> · {r.explicacao || r.motivo}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
